import { useMe } from "@@/authentication/me/me-context";
import { useProvidersForUser } from "@@/providers/state/queries/providers-for-user/use-providers-for-user";
import {
    createSocketEventListeners,
    useSocket,
} from "@@/shared/sockets/use-socket";
import type { MessageSocketEvent } from "@towni/common";
import { useCallback, useMemo, useRef } from "react";

type Params = {
    /** Callback function to handle order events */
    onMessageEvent: (
        event: MessageSocketEvent,
        room: string | undefined,
    ) => void | Promise<void>;
    /** Enable verbose logging for socket events */
    verbose?: boolean;
};
/**
 * Custom hook to listen to real-time order updates for the current user and their providers.
 * Handles frequently changing callbacks by using useRef to store the latest callback.
 *
 * @param params - Configuration object containing callback and options
 * @returns Socket connection status and cleanup
 */
const useMessageSocketEventsForMe = (params: Params) => {
    const [me] = useMe();
    const { onMessageEvent, verbose } = params;
    const [providersForUser] = useProvidersForUser(me?._id);

    // Store the latest callback in a ref to avoid unnecessary re-renders
    const onMessageEventRef = useRef(onMessageEvent);
    onMessageEventRef.current = onMessageEvent;

    // Create a stable callback that reads from the ref at execution time
    const stableOnMessageEvent = useCallback(
        (event: MessageSocketEvent, room: string | undefined) => {
            return onMessageEventRef.current(event, room);
        },
        [],
    );

    const socketListeners = useMemo(() => {
        if (!me) return undefined;
        return [
            ...createSocketEventListeners<MessageSocketEvent>({
                eventTypes: [
                    "message-event:message-created",
                    "message-event:message-updated",
                ],
                rooms: [
                    me._id,
                    ...providersForUser.map(provider => provider._id),
                ],
                onEvent: stableOnMessageEvent,
            }),
        ];
    }, [me, providersForUser, stableOnMessageEvent]);

    return useSocket(socketListeners, { verbose });
};

export { useMessageSocketEventsForMe };
